import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Linkedin, Instagram } from 'lucide-react';
import { SiX, SiTiktok } from 'react-icons/si';
import { HERO_CONTENT,COMPANY_INFO, COMPANY_LINKS } from '../../constants';
import { handlePageNavigation } from '../../utils/navigation';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-secondary-900 text-white">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 1), rgba(0, 0, 0, 1)), url('${HERO_CONTENT.backgroundImage}')`,
          backgroundSize: '150% auto',
          zIndex: 20
        }}
      ></div>
      {/* Main Footer Content */}
      <div className="container section-padding relative z-40" > 

        {/* Mobile-First Layout */}
        <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-8">
          {/* Company Info - Enhanced for Mobile */}
          <div className="space-y-6 text-center md:text-left">
            <div>
              <img 
                src="/assets/images/branding/M-Telecommunication_logo_FA_3.svg" 
                alt={COMPANY_INFO.fullName}
                className="h-10 md:h-12 w-auto mb-4 brightness-0 invert mx-auto md:mx-0"
              />

              <p className="text-secondary-300 text-sm leading-relaxed max-w-xs mx-auto md:mx-0">
                {COMPANY_INFO.description}
              </p>
            </div>
            
            {/* Social Media - Mobile Optimized */}
            <div className="flex justify-center md:justify-start space-x-3">
              <a 
                href={COMPANY_INFO.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={COMPANY_INFO.social.x}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                aria-label="X (formerly Twitter)"
              >
                <SiX className="w-4 h-4" />
              </a>
              <a 
                href={COMPANY_INFO.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={COMPANY_INFO.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                aria-label="TikTok"
              >
                <SiTiktok className="w-4 h-4" />
              </a>
              <a 
                href={COMPANY_INFO.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Contact Info - Priority on Mobile */}
          <div className="order-first md:order-last">
            <h4 className="text-lg font-semibold mb-6 text-center md:text-left">Contact Info</h4>
            <div className="space-y-4">
              {/* Address - Condensed for Mobile */}
              <div className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-3 text-center md:text-left">
                <MapPin className="w-5 h-5 text-primary-400 flex-shrink-0 mx-auto sm:mx-0 sm:mt-0.5" />
                <div className="text-secondary-300 text-sm">
                  <p className="font-medium">{COMPANY_INFO.address.street}</p>
                  <p>{COMPANY_INFO.address.postcode} {COMPANY_INFO.address.city}</p>
                  <p>{COMPANY_INFO.address.state}, {COMPANY_INFO.address.country}</p>
                </div>
              </div>
              
              {/* Phone - Touch-Friendly */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 text-center md:text-left">
                <Phone className="w-5 h-5 text-primary-400 flex-shrink-0 mx-auto sm:mx-0" />
                <a 
                  href={`tel:${COMPANY_INFO.contact.phone}`}
                  className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm font-medium bg-secondary-800/50 md:bg-transparent py-2 px-4 md:p-0 rounded-lg md:rounded-none inline-block"
                >
                  {COMPANY_INFO.contact.phone}
                </a>
              </div>
              
              {/* Email - Touch-Friendly */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 text-center md:text-left">
                <Mail className="w-5 h-5 text-primary-400 flex-shrink-0 mx-auto sm:mx-0" />
                <a 
                  href={`mailto:${COMPANY_INFO.contact.email}`}
                  className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm font-medium bg-secondary-800/50 md:bg-transparent py-2 px-4 md:p-0 rounded-lg md:rounded-none inline-block break-all sm:break-normal"
                >
                  {COMPANY_INFO.contact.email}
                </a>
              </div>
              
              {/* Business Hours - Condensed for Mobile */}
              <div className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-3 text-center md:text-left">
                <Clock className="w-5 h-5 text-primary-400 flex-shrink-0 mx-auto sm:mx-0 sm:mt-0.5" />
                <div className="text-secondary-300 text-sm space-y-1">
                  <p><span className="font-medium">Mon-Fri:</span> {COMPANY_INFO.businessHours.weekdays}</p>
                  <p><span className="font-medium">Sat:</span> {COMPANY_INFO.businessHours.saturday}</p>
                  <p><span className="font-medium">Sun:</span> {COMPANY_INFO.businessHours.sunday}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Links - Mobile Grid */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold mb-6">Company</h4>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-3 max-w-xs mx-auto md:max-w-none">
              {COMPANY_LINKS.map((item) => (
                <Link 
                  key={item.label}
                  to={item.href}
                  className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm bg-secondary-800/30 md:bg-transparent py-2 px-3 md:p-0 rounded-lg md:rounded-none block hover:bg-secondary-700/50 md:hover:bg-transparent"
                  onClick={handlePageNavigation}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <img 
              src="/assets/images/branding/md-logo.png" 
              alt="MD Logo"
              className="mt-6 h-16 md:h-20 w-auto mx-auto md:mx-0 brightness-0 invert opacity-80"
            />
          </div>

          {/* Services - Mobile Grid */}
          <div className="text-center md:text-left">
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <div className="space-y-3">
              <Link 
                to="/services/gpu-clusters"
                className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm bg-secondary-800/30 md:bg-transparent py-2 px-3 md:p-0 rounded-lg md:rounded-none block hover:bg-secondary-700/50 md:hover:bg-transparent"
                onClick={handlePageNavigation}
              >
                Built-to-Suit GPU Clusters
              </Link>
              <Link 
                to="/services/gpu-leasing"
                className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm bg-secondary-800/30 md:bg-transparent py-2 px-3 md:p-0 rounded-lg md:rounded-none block hover:bg-secondary-700/50 md:hover:bg-transparent"
                onClick={handlePageNavigation}
              >
                NVIDIA GPU Node Leasing
              </Link>
              <Link 
                to="/services/on-demand"
                className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm bg-secondary-800/30 md:bg-transparent py-2 px-3 md:p-0 rounded-lg md:rounded-none block hover:bg-secondary-700/50 md:hover:bg-transparent"
                onClick={handlePageNavigation}
              >
                On-Demand Cloud GPU Compute
              </Link>
              <Link 
                to="/services/ai-solutions"
                className="text-secondary-300 hover:text-primary-400 transition-colors duration-200 text-sm bg-secondary-800/30 md:bg-transparent py-2 px-3 md:p-0 rounded-lg md:rounded-none block hover:bg-secondary-700/50 md:hover:bg-transparent"
                onClick={handlePageNavigation}
              >
                AI Software Solutions
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-secondary-800 relative z-40">
        <div className="container py-6">
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between text-center md:text-left">
            <div className="text-secondary-400 text-sm">
              <p>&copy; {currentYear} {COMPANY_INFO.fullName}.</p>
              <p className="md:inline md:ml-1">All rights reserved.</p>
            </div>
            <div className="flex flex-col space-y-2 md:space-y-0 md:flex-row md:space-x-6">
              <Link 
                to="/privacy" 
                className="text-secondary-400 hover:text-primary-400 transition-colors duration-200 text-sm bg-secondary-800/30 md:bg-transparent py-2 px-4 md:p-0 rounded-lg md:rounded-none hover:bg-secondary-700/50 md:hover:bg-transparent"
                onClick={handlePageNavigation}
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
